import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";

interface Contact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  type: 'client' | 'prospect';
}

interface ListType {
  id: number;
  name: string;
  description?: string;
  contactIds: string[];
}

interface EditListDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  list: ListType | null;
  listName: string;
  onListNameChange: (value: string) => void;
  listDescription: string;
  onListDescriptionChange: (value: string) => void;
  selectedContacts: Set<string>;
  onSelectedContactsChange: (contacts: Set<string>) => void;
  availableContacts: Contact[];
  onSave: (data: { name: string; description?: string; contactIds: string[] }) => void;
  onCancel: () => void;
  isLoading?: boolean;
  contactType: 'email' | 'sms';
}

export function EditListDialog({
  isOpen,
  onOpenChange,
  list,
  listName,
  onListNameChange,
  listDescription,
  onListDescriptionChange,
  selectedContacts,
  onSelectedContactsChange,
  availableContacts,
  onSave,
  onCancel,
  isLoading = false,
  contactType,
}: EditListDialogProps) {
  const { toast } = useToast();

  const contactKey = (contact: Contact) => `${contact.type}-${contact.id}`;
  const contactsInList = availableContacts.filter(contact => 
    selectedContacts.has(contactKey(contact))
  );
  const contactsAvailable = availableContacts.filter(contact => 
    !selectedContacts.has(contactKey(contact))
  ).slice(0, 50); // Limit to first 50 for performance

  const contactColumnHeader = contactType === 'email' ? 'Email' : 'Phone';
  const getContactValue = (contact: Contact) => contactType === 'email' ? contact.email : contact.phone;

  const handleSave = () => {
    if (!listName.trim()) {
      toast({ 
        title: "Validation Error", 
        description: "List name is required",
        variant: "destructive" 
      });
      return;
    }
    if (list) {
      const contactIdsArray = Array.from(selectedContacts);
      onSave({
        name: listName.trim(),
        description: listDescription.trim() || undefined,
        contactIds: contactIdsArray,
      });
    }
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        onOpenChange(open);
        if (!open) {
          onCancel();
        }
      }}
    >
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Smart List</DialogTitle>
          <DialogDescription>
            Update the name, description, and contacts in your smart list.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="editListName">List Name</Label>
            <Input
              id="editListName"
              placeholder="List name"
              value={listName}
              onChange={(e) => onListNameChange(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="editListDescription">Description (optional)</Label>
            <Textarea
              id="editListDescription"
              placeholder="Description"
              value={listDescription}
              onChange={(e) => onListDescriptionChange(e.target.value)}
              className="mt-1"
            />
          </div>
          
          {/* Contacts in List Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Contacts in List ({selectedContacts.size})</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const allContactIds = new Set(availableContacts.map(c => contactKey(c)));
                  onSelectedContactsChange(allContactIds);
                }}
                className="text-xs"
              >
                Add All Available
              </Button>
            </div>
            
            {selectedContacts.size > 0 ? (
              <div className="border rounded-md max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>{contactColumnHeader}</TableHead>
                      <TableHead>Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contactsInList.map((contact) => (
                      <TableRow key={contactKey(contact)}>
                        <TableCell>
                          <Checkbox
                            checked={true}
                            onCheckedChange={() => {
                              const newSet = new Set(selectedContacts);
                              newSet.delete(contactKey(contact));
                              onSelectedContactsChange(newSet);
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{contact.name}</TableCell>
                        <TableCell>{getContactValue(contact)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{contact.type}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="border rounded-md p-8 text-center text-muted-foreground">
                <p>No contacts in this list. Add contacts from the available contacts below.</p>
              </div>
            )}
          </div>

          {/* Available Contacts to Add */}
          <div className="space-y-2">
            <Label>Available Contacts (Select to add to list)</Label>
            <div className="border rounded-md max-h-64 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>{contactColumnHeader}</TableHead>
                    <TableHead>Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contactsAvailable.length > 0 ? (
                    contactsAvailable.map((contact) => (
                      <TableRow key={contactKey(contact)}>
                        <TableCell>
                          <Checkbox
                            checked={false}
                            onCheckedChange={() => {
                              const newSet = new Set(selectedContacts);
                              newSet.add(contactKey(contact));
                              onSelectedContactsChange(newSet);
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{contact.name}</TableCell>
                        <TableCell>{getContactValue(contact)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{contact.type}</Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-4">
                        All available contacts are already in the list
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button 
              variant="outline" 
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update List'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


